
import type { ModuleNavItem as SidebarNavItem } from '@/modules';
import { modules, sidebarNavItems } from '@/modules'
import { useNetworkingVocabulary } from '@/features/networking'
import { useDigitalProductContextOptional } from '@/shared/contexts/DigitalProductContext'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { isNavigablePageWithVocabulary, toNavItem } from '@/shared/lib/nav/module-type-config'

const INSIGHTS_ITEM = modules.find(m => m.slug === 'insights')!

export interface AllowedSidebarNavItem extends SidebarNavItem {
  allowed: boolean
  active: boolean
}

export interface UseNavigationResult {
  basePathWithId: string
  startAt: string | null
  sidebarNavItems: AllowedSidebarNavItem[]
  /**
   * `false` enquanto os destinos reais forem desconhecidos. O que esta lista
   * nunca mais faz é preencher o vazio com módulos padrão navegáveis.
   */
  itemsKnown: boolean
  navigateTo: (path: string) => void
}

export const useNavigation = (): UseNavigationResult => {
  const router = useAppRouter()
  // Optional: a bottomnav monta antes do evento, junto com a topbar. Rota,
  // config e `pages` já existem nesse momento; o que falta é só o dado do
  // evento, e a trava de módulo abaixo trata a ausência.
  const contexto = useDigitalProductContextOptional()
  const basePathWithId = contexto?.config?.basePathWithId ?? ''
  const startAt = contexto?.lockRules?.startAt
  const isAllowedModule = contexto?.lockRules?.isAllowedModule
  const pages = contexto?.pages
  const pagesKnown = contexto?.pagesKnown ?? false
  const vocabulary = useNetworkingVocabulary()

  const pathname =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(basePathWithId, '')
      : ''

  const toAllowed = (navItem: SidebarNavItem): AllowedSidebarNavItem => {
    const active = ['', '/'].includes(navItem.href)
      ? ['', '/'].includes(pathname)
      : pathname.startsWith(navItem.href)
    return { ...navItem, allowed: true, active }
  }

  let allowedSidebarNavItems: AllowedSidebarNavItem[]
  let itemsKnown = true

  if (pages && pages.length > 0) {
    const hasNetworkingVocabulary = vocabulary.length > 0
    const navigablePages = pages.filter(page =>
      isNavigablePageWithVocabulary(page, hasNetworkingVocabulary)
    )
    const dynamicItems = navigablePages.map(page => toAllowed(toNavItem(page)))
    allowedSidebarNavItems = [toAllowed(INSIGHTS_ITEM), ...dynamicItems]
  } else if (pagesKnown && isAllowedModule) {
    // Resposta confirmada e sem página configurada é conhecimento: o produtor
    // não montou menu próprio, e os módulos que a trava permite SÃO os
    // destinos reais. `pagesKnown` separa este caso do "ainda não sei".
    allowedSidebarNavItems = sidebarNavItems
      .filter((navItem: SidebarNavItem) => isAllowedModule(navItem.slug))
      .map(toAllowed)
  } else {
    // Lista VAZIA de propósito: preencher com os módulos padrão, como se
    // fazia aqui, renderizava três alvos de toque navegáveis apontando para o
    // destino errado, que trocavam de identidade quando `pages` chegava. Quem
    // renderiza usa `itemsKnown` para segurar a geometria sem oferecer
    // navegação — ver `BottomNav`.
    allowedSidebarNavItems = []
    itemsKnown = false
  }

  const navigateTo = (path: string) => {
    router.push(`${basePathWithId}${path}`)
  }

  return {
    basePathWithId,
    startAt,
    sidebarNavItems: allowedSidebarNavItems,
    itemsKnown,
    navigateTo
  }
}
